import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import {
  ArrowRight, ArrowLeft, Shield, DollarSign, Eye, Ban, CheckCircle,
  Lock, Sparkles, FolderOpen, FileText, ClipboardCheck, Wallet,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: '-80px' } };

// ── Annotation Callout ──
// Absolute-positioned tag pointing at a specific pixel spot on the screenshot below.
function Callout({ x, y, label, tone = 'purple', arrow = 'left' }) {
  const tones = {
    purple: 'bg-[#6C3AED] text-white shadow-[0_10px_40px_-8px_rgba(108,58,237,0.7)]',
    emerald: 'bg-emerald-500 text-white shadow-[0_10px_40px_-8px_rgba(16,185,129,0.6)]',
    amber: 'bg-amber-500 text-white shadow-[0_10px_40px_-8px_rgba(245,158,11,0.6)]',
  };
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.85 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: 0.2 }}
      className="absolute z-20 flex items-center gap-2"
      style={{ left: `${x}%`, top: `${y}%` }}>
      {arrow === 'left' && <div className="w-8 sm:w-12 h-[2px] bg-current opacity-60" style={{ color: tone === 'purple' ? '#6C3AED' : tone === 'emerald' ? '#10b981' : '#f59e0b' }} />}
      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${tones[tone]}`}>
        <span className="w-1.5 h-1.5 rounded-full bg-white/80" />
        {label}
      </span>
      {arrow === 'right' && <div className="w-8 sm:w-12 h-[2px] bg-current opacity-60" style={{ color: tone === 'purple' ? '#6C3AED' : tone === 'emerald' ? '#10b981' : '#f59e0b' }} />}
    </motion.div>
  );
}

// ── Feature Row (screenshot + text) ──
function FeatureRow({ eyebrow, title, body, image, callouts = [], reverse = false, bullets = [] }) {
  return (
    <motion.div {...fadeUp} transition={{ duration: 0.6 }}
      className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${reverse ? 'lg:[&>div:first-child]:order-2' : ''}`}>
      <div className="lg:col-span-5 space-y-5">
        <p className="text-[#A78BFA] text-xs font-bold tracking-[0.2em] uppercase">{eyebrow}</p>
        <h3 className="text-3xl sm:text-4xl font-bold text-white leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
          {title}
        </h3>
        <p className="text-white/50 text-base leading-relaxed">{body}</p>
        {bullets.length > 0 && (
          <ul className="space-y-3 pt-2">
            {bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-[#6C3AED]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-3 h-3 text-[#A78BFA]" />
                </div>
                <span className="text-white/70 text-sm">{b}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="lg:col-span-7 relative">
        <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_30px_80px_-20px_rgba(108,58,237,0.4)] bg-gradient-to-br from-[#181C2C] to-[#0B0F19]">
          <img src={image} alt="" loading="lazy" className="w-full h-auto block" />
          {callouts.map((c, i) => <Callout key={i} {...c} />)}
        </div>
        {/* soft purple glow */}
        <div className="absolute -inset-4 bg-[#6C3AED]/10 blur-3xl -z-10 rounded-3xl" />
      </div>
    </motion.div>
  );
}

// ── Step chip (in "How it works" strip) ──
function Step({ n, icon: Icon, title, body }) {
  return (
    <div className="relative">
      <div className="w-12 h-12 rounded-xl bg-[#6C3AED]/15 border border-[#6C3AED]/30 flex items-center justify-center mb-4">
        <Icon className="w-5 h-5 text-[#A78BFA]" />
      </div>
      <p className="text-[#A78BFA] text-[10px] font-bold tracking-[0.2em] uppercase mb-1.5">Step {n}</p>
      <h4 className="text-white text-base font-semibold mb-1.5" style={{ fontFamily: "'Playfair Display', serif" }}>{title}</h4>
      <p className="text-white/40 text-sm leading-relaxed">{body}</p>
    </div>
  );
}

export default function CreatorsLanding() {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[#0B0F19] text-white" data-testid="creators-landing-page">
      {/* HEADER */}
      <header className="fixed top-0 w-full z-50 bg-[#0B0F19]/90 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
            <div className="w-8 h-8 rounded-lg bg-[#6C3AED] flex items-center justify-center">
              <span className="text-white font-bold text-sm">M</span>
            </div>
            <span className="text-white font-bold text-lg tracking-tight">MyLikeness</span>
          </Link>
          <div className="flex items-center gap-4">
            {user ? (
              <Button onClick={() => navigate(user.role === 'creator' ? '/creator' : user.role === 'admin' ? '/admin' : '/agency')}
                className="bg-[#6C3AED] hover:bg-[#5B2ED4] text-white text-sm font-semibold h-10 px-5 rounded-full">
                Go to Dashboard
              </Button>
            ) : (
              <>
                <Link to="/login" className="text-sm font-medium text-white/80 hover:text-white transition-colors hidden sm:block">Log in</Link>
                <Button onClick={() => navigate('/signup')} className="bg-[#6C3AED] hover:bg-[#5B2ED4] text-white text-sm font-semibold h-10 px-6 rounded-full" data-testid="nav-get-started-btn">
                  Get Started
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-40 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-[#6C3AED]/25 blur-[120px] rounded-full" />
          <div className="absolute top-0 right-1/4 w-[400px] h-[400px] bg-[#8B5CF6]/20 blur-[100px] rounded-full" />
        </div>
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 relative">
          <Link to="/" className="inline-flex items-center gap-2 text-xs text-white/40 hover:text-white/70 transition-colors mb-6" data-testid="back-to-home">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to home
          </Link>
          <motion.div {...fadeUp} transition={{ duration: 0.5 }} className="max-w-3xl">
            <p className="text-[#A78BFA] text-xs font-bold tracking-[0.25em] uppercase mb-6">For Creators</p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Have your <span className="italic text-[#8B5CF6]">likeness</span><br />
              work <span className="bg-gradient-to-r from-[#8B5CF6] to-[#A78BFA] bg-clip-text text-transparent">for you</span>.
            </h1>
            <p className="text-white/60 text-lg leading-relaxed max-w-xl mb-10">
              Your face is worth more than a single project. License your likeness to trusted agencies on your terms — with full control, transparent payouts, and iron-clad protection.
            </p>
            <div className="flex items-center gap-4 flex-wrap">
              <Button onClick={() => navigate('/signup')} data-testid="hero-cta-signup"
                className="bg-[#6C3AED] hover:bg-[#5B2ED4] text-white h-12 px-7 rounded-full text-sm font-bold shadow-lg shadow-[#6C3AED]/30">
                Create your creator profile <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <button onClick={() => navigate('/agencies')} className="text-white/50 hover:text-white text-sm font-semibold inline-flex items-center gap-1.5 transition-colors">
                Are you an agency? <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS — 4 steps */}
      <section className="py-16 border-t border-white/5 bg-[#0B0F19]">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <motion.div {...fadeUp} transition={{ duration: 0.5 }} className="mb-14 max-w-2xl">
            <p className="text-[#A78BFA] text-xs font-bold tracking-[0.2em] uppercase mb-4">How It Works</p>
            <h2 className="text-4xl sm:text-5xl font-bold leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              From profile to payout, <span className="italic text-[#8B5CF6]">simple.</span>
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <Step n="1" icon={FolderOpen} title="Build your Likeness Pack" body="Upload reference photos, videos, and voice samples that stay private until you approve a deal." />
            <Step n="2" icon={FileText} title="Review proposals" body="Agencies send offers with clear terms — platforms, exclusivity, duration, price. You accept, counter, or decline." />
            <Step n="3" icon={ClipboardCheck} title="Approve every piece of content" body="Content is created and reviewed by you before anything goes public. If it's not right, it doesn't get released." />
            <Step n="4" icon={Wallet} title="Get paid to your bank" body="Funds are held safely, released when you approve, and deposited straight to your account via Stripe." />
          </div>
        </div>
      </section>

      {/* FEATURE DEEP DIVES */}
      <section className="py-24 border-t border-white/5">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 space-y-32">
          <FeatureRow
            eyebrow="Your Likeness Pack"
            title="Private by default. Shared only on your terms."
            body="A dedicated vault for the reference material agencies need to create with your likeness — images, video, audio, PDFs. Nothing is shared until you accept a deal, and even then, you pick exactly which assets go to which agency."
            image="/marketing/creator-likeness.jpeg"
            bullets={[
              'Images, video, audio and PDF supported',
              'Per-deal asset selection — never a blanket handover',
              'Assets never appear in a public gallery',
            ]}
            callouts={[
              { x: 55, y: 20, label: 'Private library', tone: 'purple' },
              { x: 42, y: 62, label: 'Auto-organised by type', tone: 'emerald' },
            ]}
          />

          <FeatureRow
            eyebrow="Review Proposals"
            title="Every offer, one clean inbox."
            body="See exactly what each agency is asking for — platforms, territory, exclusivity, duration, and payout — before saying yes. Filter by status, jump into full details, and counter without back-and-forth emails."
            image="/marketing/creator-proposals.jpeg"
            reverse
            bullets={[
              'Clear headline offer and terms up-front',
              'Verified agency badge on every proposal',
              'One-click accept, decline, or send counter',
            ]}
            callouts={[
              { x: 45, y: 10, label: 'Filter by status', tone: 'purple' },
              { x: 55, y: 20, label: 'Verified agency', tone: 'emerald' },
              { x: 35, y: 30, label: 'Full offer terms', tone: 'amber' },
            ]}
          />

          <FeatureRow
            eyebrow="Active Deals"
            title="Track everything, from held funds to release."
            body="Every accepted deal shows up in Engagements with a live status: funded, content pending, awaiting your approval, ready for release. You always know where things stand and where the money is."
            image="/marketing/creator-engagements.jpeg"
            bullets={[
              'Live status per deal — no guesswork',
              'Content needing your review is flagged instantly',
              'Held-funds badge means the money is already secured',
            ]}
            callouts={[
              { x: 40, y: 34, label: 'Funds held safely', tone: 'emerald' },
              { x: 60, y: 44, label: 'Needs your review', tone: 'amber' },
            ]}
          />

          <FeatureRow
            eyebrow="Payouts"
            title="Straight to your bank. Zero chasing."
            body="Money moves through a 5-step flow: proposal accepted → agency funds it → content produced → you approve → funds released to your Stripe-connected bank account within 2–7 days."
            image="/marketing/creator-payouts.jpeg"
            reverse
            bullets={[
              'Held in escrow so agencies can\'t pull the rug',
              'Withdraw via Stripe Connect (bank deposit, 2–7 days)',
              'Lifetime earnings, funds secured, ready-to-release — all in one view',
            ]}
            callouts={[
              { x: 40, y: 42, label: 'Escrow-secured', tone: 'purple' },
              { x: 5, y: 74, label: '5-step payout flow', tone: 'emerald' },
            ]}
          />

          <FeatureRow
            eyebrow="Permissions & Verification"
            title="Rules that follow you into every deal."
            body="Set global ban categories, content restrictions, and licensing preferences once. Every proposal is validated against your rules — if an agency requests something you've banned, they can't send it."
            image="/marketing/creator-settings.jpeg"
            bullets={[
              'Ban categories: alcohol, gambling, politics, and 12 more',
              'Verified identity via Stripe — trusted by every agency',
              'Notifications & security controls in one place',
            ]}
            callouts={[
              { x: 30, y: 15, label: 'One-time setup', tone: 'purple' },
              { x: 62, y: 55, label: 'Stripe-verified', tone: 'emerald' },
            ]}
          />
        </div>
      </section>

      {/* PROTECTION BAR */}
      <section className="py-16 border-t border-white/5 bg-gradient-to-b from-[#0B0F19] to-[#101528]">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <motion.div {...fadeUp} transition={{ duration: 0.5 }} className="text-center mb-10">
            <p className="text-[#A78BFA] text-xs font-bold tracking-[0.2em] uppercase mb-3">Built to protect you</p>
            <h2 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
              Ironclad by design.
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { icon: Shield, title: 'Escrow-first payments', body: 'Agencies fund the deal before creation starts. No content, no money released, no confusion.' },
              { icon: Ban, title: 'Content veto power', body: 'Every piece of content is reviewed by you. Reject anything that doesn\'t match the agreement.' },
              { icon: Lock, title: 'Contract-backed licenses', body: 'Every deal is a signed PDF agreement, versioned and auditable, stored forever.' },
              { icon: Eye, title: 'Full transparency', body: 'See exactly how your likeness is being used, where, and until when — audit trail per license.' },
              { icon: DollarSign, title: 'Transparent fees', body: 'Flat platform fee. Stripe fees clearly shown. What you see is what lands in your bank.' },
              { icon: Sparkles, title: 'Multiple license types', body: 'One-time, subscription, hybrid, or royalty — pick the structure that matches the deal.' },
            ].map((f, i) => (
              <motion.div key={i} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.05 }}
                className="p-5 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-[#6C3AED]/30 transition-colors">
                <div className="w-10 h-10 rounded-xl bg-[#6C3AED]/15 flex items-center justify-center mb-3">
                  <f.icon className="w-4.5 h-4.5 text-[#A78BFA]" />
                </div>
                <h4 className="text-white font-semibold text-base mb-1.5" style={{ fontFamily: "'Playfair Display', serif" }}>{f.title}</h4>
                <p className="text-white/50 text-sm leading-relaxed">{f.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10 text-center">
          <motion.div {...fadeUp} transition={{ duration: 0.5 }}>
            <h2 className="text-4xl sm:text-5xl font-bold mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
              Ready to license your <span className="italic text-[#8B5CF6]">likeness?</span>
            </h2>
            <p className="text-white/60 text-lg mb-8 max-w-xl mx-auto">
              Takes about 5 minutes to set up. No cost to join. You only see verified agencies once you&apos;re live.
            </p>
            <Button onClick={() => navigate('/signup')} data-testid="footer-cta-signup"
              className="bg-[#6C3AED] hover:bg-[#5B2ED4] text-white h-14 px-9 rounded-full text-base font-bold shadow-lg shadow-[#6C3AED]/30">
              Create your creator profile <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-10 border-t border-white/5 bg-[#080B14]">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/30 text-xs">© {new Date().getFullYear()} MyLikeness · Licensed presence, verified proof</p>
          <div className="flex items-center gap-5 flex-wrap justify-center md:justify-end">
            <Link to="/" className="text-white/40 hover:text-white/80 text-xs transition-colors">Home</Link>
            <Link to="/agencies" className="text-white/40 hover:text-white/80 text-xs transition-colors">For Agencies</Link>
            <Link to="/terms" className="text-white/40 hover:text-white/80 text-xs transition-colors">Terms</Link>
            <Link to="/privacy" className="text-white/40 hover:text-white/80 text-xs transition-colors">Privacy</Link>
            <Link to="/acceptable-use" className="text-white/40 hover:text-white/80 text-xs transition-colors">Acceptable Use</Link>
            <Link to="/trust" className="text-white/40 hover:text-white/80 text-xs transition-colors">Trust &amp; Safety</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
